import axios from 'axios';
import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { capitalizeFirstLetterOfEachWord, getPercentageChangeV2 } from 'utils/helperMethods';
import { PVMUrls } from 'config/urls';
import _ from 'lodash';

const errorMessage: string = 'Something went wrong!';

interface Props {
    queryKey: any[];
    variables?: any;
    queryConfig?: {
        enabled?: boolean;
    };
    plansLabel: any;
}

interface outputType {
    data: any;
    loading: boolean;
    error: any;
}

function useFetchFeatureGroupsData({ queryKey, variables = {}, queryConfig = {}, plansLabel }: Props): outputType {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<any | null>(null);

    // Queries
    const {
        data: apiData,
        isFetching,
        isSuccess,
        fetchStatus,
        error: apiError,
    } = useQuery({
        queryKey,
        queryFn: async () => {
            //initializing states
            setData(null);
            setError(null);
            setLoading(true);

            try {
                const data = await axios({
                    method: 'post',
                    url: PVMUrls.FeatureGroupsDataUrl,
                    data: { ...variables },
                });
                return data;
            } catch (error) {
                onFetchError(error);
            }
        },
        ...queryConfig,
        //  enabled: false,
    });

    const apiLoading = isFetching || fetchStatus === 'paused';
    const apiSuccess = !apiLoading && !apiError && isSuccess;

    //error handling
    if (!apiLoading && apiError && !error) {
        // if (onError) return onError(apiError);
        onFetchError(apiError);
    }
    useEffect(() => {
        //success handling
        if (apiSuccess && apiData?.data) {
            if (error) setError(null);
            // if (onComplete) return onComplete(apiData?.data);
            onFetchComplete(apiData.data);
        }
    }, [apiData?.data, apiSuccess]);

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const parsedData: any = { ...getMinAndMaxValues(payload) };
            const { data, headers } = parseGropedData(payload);
            parsedData.data = data;
            parsedData.headers = headers;
            setData(parsedData);
        } catch (error) {
            onFetchError(error);
            console.error(errorMessage, error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        setData(null);
        setLoading(false);
        setError(error);
    }
    const outputData: outputType = {
        loading,
        error,
        data,
    };
    return outputData;
}

export { useFetchFeatureGroupsData };

const getMinAndMaxValues = (obj: any) => {
    let minVal = Infinity;
    let maxVal = -Infinity;
    Object.keys(obj).forEach((i) => {
        Object.keys(obj[i]).forEach((k) => {
            const v = obj[i][k];
            minVal = Math.min(minVal, v);
            maxVal = Math.max(maxVal, v);
        });
    });
    return { minVal, maxVal };
};

const parseGropedData = (obj: any) => {
    const result: Array<any> = [];

    const processGroupData = (groupData: any, disableHover: boolean, cols: Array<any>) => {
        if (cols.length > 0) {
            cols.forEach((key, index: number) => {
                const salesVolumeAbs = groupData[key];
                const newObj = {
                    sales: salesVolumeAbs,
                    apiParamKey: key,
                };
                result.push(newObj);
            });
        } else {
            Object.keys(groupData)?.forEach((key, index: number) => {
                const salesVolumeAbs = groupData[key];
                const newObj = {
                    sales: salesVolumeAbs,
                    apiParamKey: key,
                };
                result.push(newObj);
            });
        }
    };

    processGroupData(obj.total_sales_data, true, []);
    processGroupData(obj.attribution_group_data, false, [
        'price_impact',
        'total_volume_impact',
        'mix_impact',
        'others',
    ]);

    // keep compare values at the corners of the arrays
    const firstCompareItem = result[0];
    const secondCompareItem = result[1];
    const rest = result.slice(2);
    const dataArray = [secondCompareItem, ...rest, firstCompareItem];

    const firstItemSales = dataArray[0].sales;
    const firstRow: any = { scale: null };
    const secondRow: any = { scale: [] };
    const thirdRow: any = { scale: null };
    const lastRow: any = { scale: null };
    const headers: string[] = [''];

    let previous: number = 0;
    dataArray.forEach((item: any, index: number) => {
        // both the corners will start from 0
        if (index === dataArray.length - 1) {
            previous = 0;
        }
        firstRow[item.apiParamKey] =
            index === 0
                ? null
                : index === dataArray.length - 1
                ? getPercentageChangeV2(dataArray[0].sales, dataArray[dataArray.length - 1].sales)
                : _.round((item.sales / firstItemSales) * 100, 2);

        secondRow[item.apiParamKey] = [
            {
                type: item.apiParamKey,
                change: [previous, previous + item.sales],
            },
        ];
        // update the previous value
        previous += item.sales;

        thirdRow[item.apiParamKey] = [capitalizeFirstLetterOfEachWord(item.apiParamKey, '_')];
        headers.push(capitalizeFirstLetterOfEachWord(item.apiParamKey, '_'));

        lastRow[item.apiParamKey] =
            index === 0
                ? null
                : index === dataArray.length - 1
                ? [getPercentageChangeV2(dataArray[0].sales, dataArray[dataArray.length - 1].sales)]
                : [_.round((item.sales / firstItemSales) * 100, 2)];
    });

    const data = [firstRow, secondRow, thirdRow, lastRow];

    return { data, headers };
};
